import { Box } from "@radix-ui/themes";
import { on } from "events";
import { useEffect, useState } from "react";
import { RxDotFilled } from "react-icons/rx";

interface MinMaxScaleProps {
  minLabel: string;
  maxLabel: string;
  minValue: number;
  maxValue: number;
  step: number;
  value: number;
  inLine: boolean;
  onScale: (value: number) => void;
}

const InlineMinMaxScale = ({ minLabel, maxLabel, minValue, maxValue, step, value, onScale }: MinMaxScaleProps) => {
  const generateDots = (min: number, max: number, step: number) => {
    const dots = [];
    for (let i = min; i <= max; i += step) {
      dots.push(i);
    }
    return dots;
  };

  const dots = generateDots(minValue, maxValue, step);

  return (
    <div className="flex w-full border shadow-lg rounded-t-2xl">
      <div className="flex flex-col bg-[#D7D3E3] rounded-xl shadow-lg w-full">
        {/* Labels */}
        <div className="flex text-xs justify-between">
          <span className="text-purple-700">{minLabel}</span>
          <span className="text-purple-700">{maxLabel}</span>
        </div>

        {/* Values */}
        <div className="flex text-xs justify-between px-1 my-3">
          <span className="font-medium">{minValue}</span>
          <span className="font-medium">{Math.floor((minValue + maxValue) / 2)}</span>
          <span className="text-purple-700">{maxValue}</span>
        </div>

        {/* Slider */}
        <div className="flex justify-between relative w-full border border-purple-800 bg-white rounded-3xl">
          {dots.map((item, index) => (
            <div className="flex" key={`${item}-${index}`}>
              <RxDotFilled />
            </div>
          ))}
          <input
            type="range"
            className="absolute top-[50%] w-full accent-purple-700 appearance-none h-0 [&::-webkit-slider-thumb]:h-[20px] [&::-webkit-slider-thumb]:w-[20px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500"
            min={minValue}
            max={maxValue}
            value={value}
            step={step}
            onChange={(e) => onScale(parseInt(e.target.value))}
          />
        </div>
      </div>

      {/* Display Value */}
      <div>
        <label className="flex px-6 my-[5%] border rounded-2xl text-white py-2 bg-gradient-to-br from-purple-950 to-purple-500">
          {value}
        </label>
      </div>
    </div>
  );
};

const MinMaxScale = ({ minLabel, maxLabel, minValue, maxValue, step, value, onScale }: MinMaxScaleProps) => {
  const generateDots = (min: number, max: number, step: number) => {
    const dots = [];
    for (let i = min; i <= max; i += step) {
      dots.push(i);
    }
    return dots;
  };

  const dots = generateDots(minValue, maxValue, step);

  return (
    <div className="flex flex-col border shadow-lg max-w-3xl rounded-t-2xl">
      <div className="flex flex-col max-w-3xl px-6 py-6 bg-[#D7D3E3] rounded-xl shadow-lg">
        {/* Labels */}
        <div className="flex text-xs justify-between py-2">
          <span className="text-purple-700">{minLabel}</span>
          <span className="text-purple-700">{maxLabel}</span>
        </div>

        {/* Values */}
        <div className="flex text-xs justify-between px-1 my-3">
          <span className="font-medium">{minValue}</span>
          <span className="font-medium">{Math.floor((minValue + maxValue) / 2)}</span>
          <span className="text-purple-700">{maxValue}</span>
        </div>

        {/* Slider */}
        <div className="flex justify-between relative w-full border border-purple-800 bg-white rounded-3xl">
          {dots.map((item, index) => (
            <div className="flex" key={`${item}-${index}`}>
              <RxDotFilled />
            </div>
          ))}
          <input
            type="range"
            className="absolute top-[50%] w-full accent-purple-700 appearance-none h-0 [&::-webkit-slider-thumb]:h-[20px] [&::-webkit-slider-thumb]:w-[20px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500"
            min={minValue}
            max={maxValue}
            value={value}
            step={step}
            onChange={(e) => onScale(parseInt(e.target.value))}
          />
        </div>
      </div>

      {/* Display Value */}
      <div className="flex gap-2 justify-center mt-2">
        <span className="text-md my-auto font-medium text-purple-700">Your Response</span>
        <span className="flex px-6 border rounded-2xl text-white py-2 bg-gradient-to-br from-purple-950 to-purple-500">
          {value}
        </span>
      </div>
    </div>
  );
};

interface MinMaxRatingAssessmentProps {
  element: any;
  index: number;
  wantOptionRating: boolean;
  onScale: (option: any, scale: string, index: number) => void;
  onScaleOption: (option: any, scale: string, index: number) => void;
  onValidate?: (index: number, isValid: boolean) => void;
  isMandatory?: boolean;
}

export const MinMaxRatingAssessment = ({ element, index, onScale, onScaleOption, wantOptionRating, onValidate, isMandatory }: MinMaxRatingAssessmentProps) => {
  const [isOptionRating, setOptionRating] = useState(wantOptionRating);

  return (
    <Box>
      {!isOptionRating && (
        <MinMaxQuestion isMandatory={isMandatory} index={index}
          onValidate={onValidate} onScale={(option, scale) => onScale(option, scale, index)} element={element} />
      )}
      {isOptionRating && (
        <MinMaxOption isMandatory={isMandatory} index={index}
          onValidate={onValidate} onScale={(option, scale) => onScaleOption(option, scale, index)} element={element} />
      )}
    </Box>
  );
};

export const MinMaxQuestion = ({ element, onScale, isMandatory, onValidate, index }: { element: any; index: any; isMandatory?: boolean; onValidate?: (index: number, isValid: boolean) => void; onScale: (option: any, scale: string) => void }) => {
  const minLabel = element.minValuelabel || element.minLabel || "Min";
  const maxLabel = element.maxValuelabel || element.maxLabel || "Max";
  const minValue = parseInt(element.minValue) || 0;
  const maxValue = parseInt(element.maxValue) || 10;
  const step = parseInt(element.incrementalUnit) || 1;
  const [touched, setTouched] = useState(false);

  const value = element.answerOptions?.[0]?.value ? parseInt(element.answerOptions[0].value) : minValue;

  const option =
    element.answer?.[0] ||
    (element.answerOptions?.[0]
      ? { value: element.answerOptions[0].answerOption, isAnswer: element.answerOptions[0]?.isAnswer }
      : {});

  useEffect(() => {
    if (typeof onValidate === 'function') {
      if (isMandatory) {
        onValidate(index, touched && value !== null);
      } else {
        onValidate(index, true);
      }
    }
  }, [value, touched, index, isMandatory, onValidate]);

  return (
    <Box>
      <label className="block mb-2 text-normal font-bold text-app-gray-medium">
        {element.question}
        {element.Mandatory && <span className="text-red-600">*</span>}
      </label>
      <MinMaxScale
        value={value}
        minLabel={minLabel}
        maxLabel={maxLabel}
        minValue={minValue}
        maxValue={maxValue}
        inLine={false}
        onScale={(scale: number) => {
          setTouched(true);
          onScale(option, scale.toString());
          if (isMandatory && typeof onValidate === 'function') {
            onValidate(index, true);
          }
        }}
        step={step}
      />
    </Box>
  );
};

export const MinMaxOption = ({ element, onScale, isMandatory, onValidate, index }: { element: any; index: any; isMandatory?: boolean; onValidate?: (index: number, isValid: boolean) => void; onScale: (option: any, scale: string) => void }) => {
  const minLabel = element.minValuelabel || element.minLabel || "Min";
  const maxLabel = element.maxValuelabel || element.maxLabel || "Max";
  const minValue = parseInt(element.minValue) || 0;
  const maxValue = parseInt(element.maxValue) || 10;
  const step = parseInt(element.incrementalUnit) || 1;

  const options =
    element.answer?.map((el: any) => ({ value: el.value, isAnswer: el.isAnswer })) ||
    element.answerOptions?.map((el: any) => ({
      value: el.value,
      isAnswer: el.isAnswer || "",
      answerOption: el.answerOption,
    })) ||
    [];

  const [scales, setScales] = useState(options);

  useEffect(() => {
    if (typeof onValidate === 'function') {
      if (isMandatory) {
        const anySelected = scales.some((s) => parseInt(s.value) > 0);
        onValidate(index, anySelected);
      } else {
        onValidate(index, true);
      }
    }
  }, [scales, index, isMandatory, onValidate]);

  return (
    <Box className="w-full">
      <label className="block mb-2 text-sm mobile:text-xs font-bold text-app-gray-medium">
        {element.question}
        {element.Mandatory && <span className="text-red-600">*</span>}
      </label>

      {options.map((el: any, idx: number) => (
        <Box
          key={idx}
          className="flex flex-col mobile:flex-col tablet:flex-row justify-between w-full gap-2"
        >
          {/* Label */}
          {el.answerOption && (
            <div className="w-full mobile:w-full tablet:w-1/2 shadow-md rounded-2xl p-2 text-center tablet:text-left text-sm mobile:text-xs">
              {el.answerOption}
            </div>
          )}

          {/* Slider */}
          <div className={el.value ? "w-full tablet:w-1/2" : "w-full"}>
            <InlineMinMaxScale
              value={parseInt(el.value) || minValue}
              minLabel={minLabel}
              maxLabel={maxLabel}
              minValue={minValue}
              maxValue={maxValue}
              inLine={true}
              onScale={(scale: number) => {
                const updatedScales = [...scales];
                updatedScales[idx] = {
                  ...el,
                  value: scale.toString(),
                  isAnswer: true,
                };
                setScales(updatedScales);
                onScale(el, scale.toString());

                if (typeof onValidate === "function" && isMandatory) {
                  onValidate(index, true);
                }
              }}
              step={step}
            />
          </div>
        </Box>
      ))}
    </Box>

  );
};

export default MinMaxRatingAssessment;
